import { Core } from '@/api/Core/Core';
import { AuthError } from '@azure/msal-browser';
import {
  CompoundMetadata,
  CompoundOccupancy,
  CompoundSlots,
} from '@/types/CompoundMap';

export class CompoundOccupancyError extends Error {
  constructor(public readonly status: number) {
    super(
      status === 401 || status === 403
        ? 'Vehicle occupancy access denied. Vehicle details have been cleared.'
        : status === 409
          ? 'The map version changed. Reload the preview before requesting occupancy.'
          : status === 404
            ? 'Recorded vehicle occupancy is unavailable for this map.'
            : 'Recorded vehicle occupancy could not be refreshed.',
    );
  }
}

export class CompoundRequestError extends Error {
  constructor(public readonly status: number) {
    super(
      status === 403 || status === 401
        ? 'Candidate preview access denied.'
        : status === 409 || status === 404
          ? 'Candidate snapshot changed. Refresh the preview.'
          : 'Candidate map could not be loaded.',
    );
  }
}

const route = (siteId: number) => `/CommandCentre/sites/${siteId}/compound-map`;
class CompoundMapApi extends Core {
  private async read<T>(
    path: string,
    signal: AbortSignal,
    blob = false,
    params?: Record<string, string>,
  ): Promise<T> {
    const response = await this.get<T>(path, params, {
      signal,
      ...(blob ? { responseType: 'blob' } : {}),
    });
    // The shared interceptor resolves some errors instead of rejecting them.
    if (
      !response?.success ||
      response.code < 200 ||
      response.code >= 300 ||
      response.data == null
    ) {
      throw new CompoundRequestError(response?.code ?? 500);
    }
    signal.throwIfAborted();
    return response.data;
  }
  metadata = (siteId: number, signal: AbortSignal) =>
    this.read<CompoundMetadata>(route(siteId), signal);
  slots = (metadata: CompoundMetadata, signal: AbortSignal) =>
    this.read<CompoundSlots>(
      `${route(metadata.siteId)}/datasets/${encodeURIComponent(metadata.datasetId!)}/slots`,
      signal,
      false,
      { version: metadata.version! },
    );
  occupancy = async (metadata: CompoundMetadata, signal: AbortSignal) => {
    const response = await this.get<CompoundOccupancy>(
      `${route(metadata.siteId)}/datasets/${encodeURIComponent(metadata.datasetId!)}/occupancy`,
      { version: metadata.version! },
      {
        signal,
        // Preserve HTTP 401 through the shared success interceptor. Its error
        // branch awaits logout, which can replace the original status or never finish.
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 401,
      },
    ).catch((error: unknown) => {
      signal.throwIfAborted();
      // A 401 triggers MSAL logout in the shared interceptor. Logout can reject
      // before clearing the account; its typed auth error must still clear occupancy.
      if (error instanceof AuthError) throw new CompoundOccupancyError(401);
      const status = (error as { response?: { status?: number } } | null)
        ?.response?.status;
      throw new CompoundOccupancyError(status ?? 503);
    });
    signal.throwIfAborted();
    // The global 401 interceptor logs out and can resolve without a response.
    // Treat that outcome as access loss, never as a retainable transient failure.
    if (
      !response?.success ||
      response.code < 200 ||
      response.code >= 300 ||
      !response.data
    )
      throw new CompoundOccupancyError(response?.code ?? 401);
    const snapshot = response.data;
    if (
      snapshot.siteId !== metadata.siteId ||
      snapshot.datasetId !== metadata.datasetId ||
      snapshot.version !== metadata.version
    )
      throw new CompoundOccupancyError(409);
    if (
      !Number.isFinite(Date.parse(snapshot.asOf)) ||
      !Array.isArray(snapshot.slots) ||
      (snapshot.positionSource !== undefined &&
        !['recorded', 'dev-test'].includes(snapshot.positionSource))
    )
      throw new CompoundOccupancyError(503);
    const seen = new Set<string>();
    for (const row of snapshot.slots) {
      if (
        !row ||
        !row.slotId ||
        seen.has(row.slotId) ||
        !['occupied', 'unrecorded', 'unknown', 'conflict'].includes(
          row.status,
        ) ||
        !Array.isArray(row.vehicles) ||
        (row.status === 'occupied' && row.vehicles.length !== 1) ||
        (['unknown', 'unrecorded'].includes(row.status) &&
          row.vehicles.length !== 0) ||
        row.vehicles.some(
          (vehicle) =>
            !vehicle ||
            typeof vehicle.vin !== 'string' ||
            !Number.isFinite(vehicle.stockId),
        )
      )
        throw new CompoundOccupancyError(503);
      seen.add(row.slotId);
    }
    return snapshot;
  };
  tile = (
    metadata: CompoundMetadata,
    x: number,
    y: number,
    z: number,
    signal: AbortSignal,
  ) =>
    this.read<Blob>(
      `${route(metadata.siteId)}/datasets/${encodeURIComponent(metadata.datasetId!)}/cad/${encodeURIComponent(metadata.cad!.packageId)}/${z}/${x}/${y}.png`,
      signal,
      true,
    );
}
export const compoundMap = new CompoundMapApi();

export function validateSnapshot(
  metadata: CompoundMetadata,
  data: CompoundSlots,
): CompoundSlots {
  if (
    data.siteId !== metadata.siteId ||
    data.datasetId !== metadata.datasetId ||
    data.version !== metadata.version ||
    !data.cad ||
    data.cad.packageId !== metadata.cad?.packageId ||
    data.cad.transformId !== metadata.cad?.transformId ||
    data.cad.samplingVersion !== metadata.cad?.samplingVersion ||
    data.features.length !== metadata.summary?.total ||
    data.features.some(
      (f) => f.geometry?.type !== 'Polygon' || !f.geometry.coordinates?.length,
    )
  ) {
    throw new Error('Candidate snapshot is inconsistent. Refresh the preview.');
  }
  return data;
}
